/**
 * Global error handling utilities
 * Handles critical errors like database corruption, session expiration, etc.
 */

import { logout, softSignOut } from './auth';
import { createLogger, LogLevel } from './logger';

const logger = createLogger('ErrorHandler', LogLevel.INFO);

/**
 * Custom error types
 */
export class DatabaseError extends Error {
  constructor(message: string, public originalError?: Error) {
    super(message);
    this.name = 'DatabaseError';
  }
}

export class SessionExpiredError extends Error {
  constructor(message = 'Your session has expired. Please log in again.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/**
 * Check if an error is a Dexie database error
 */
export function isDatabaseError(error: unknown): boolean {
  if (!error) return false;

  const errorObj = error as any;

  // Check for Dexie-specific error names
  const dexieErrors = [
    'DatabaseClosedError',
    'InvalidStateError',
    'InvalidAccessError',
    'NotFoundError',
    'ConstraintError',
    'DataError',
    'TransactionInactiveError',
    'ReadOnlyError',
    'VersionError',
    'OpenFailedError',
    'UpgradeError',
  ];

  return (
    errorObj.name && dexieErrors.includes(errorObj.name) ||
    errorObj.inner?.name && dexieErrors.includes(errorObj.inner.name) ||
    errorObj.message?.includes('IDBDatabase') ||
    errorObj.message?.includes('IndexedDB')
  );
}

/**
 * Check if database was cleared/corrupted
 */
export function isDatabaseCleared(error: unknown): boolean {
  if (!error) return false;

  const errorObj = error as any;

  return (
    errorObj.name === 'NotFoundError' ||
    errorObj.name === 'DatabaseClosedError' ||
    errorObj.message?.includes('database') ||
    errorObj.message?.includes('not found')
  );
}

/**
 * Handle database errors globally
 * Shows user-friendly error and logs out if necessary
 */
export async function handleDatabaseError(error: unknown): Promise<void> {
  logger.error('Database error detected', error);

  if (isDatabaseCleared(error)) {
    // Database was cleared - need to re-login
    alert(
      'Database Error\n\n' +
      'Your local database has been cleared or corrupted. ' +
      'Please log in again to restore your data.'
    );
    await logout();
  } else if (isDatabaseError(error)) {
    // Other database error
    alert(
      'Database Error\n\n' +
      'An error occurred accessing the local database. ' +
      'The application will restart. If this persists, please contact support.'
    );

    // Reload the page to reset state
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }
}

/**
 * Handle session expiration. Soft cleanup — clears identifiers and the
 * device token but PRESERVES the local DB so any unsynced field work
 * survives a server-side revoke or cookie expiry. logout() (DB wipe) is
 * reserved for explicit user intent ("Logout and wipe device") and the
 * rule-2 UserNotFoundError branch in useSessionValidator.
 */
export async function handleSessionExpired(): Promise<void> {
  logger.warn('Session expired');

  alert(
    'Session Expired\n\n' +
    'Your session has expired. Please log in again.'
  );

  await softSignOut();
}

/**
 * Wrap async functions with error handling
 */
export function withErrorHandling<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  errorHandler?: (error: unknown) => void | Promise<void>
): T {
  return (async (...args: any[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      logger.error('Error in wrapped function', error);

      // Check for known error types
      if (isDatabaseError(error)) {
        await handleDatabaseError(error);
      } else if (error instanceof SessionExpiredError) {
        await handleSessionExpired();
      } else if (errorHandler) {
        await errorHandler(error);
      } else {
        // Re-throw if not handled
        throw error;
      }
    }
  }) as T;
}

/**
 * Setup global error handlers
 */
export function setupGlobalErrorHandlers(): void {
  // Handle unhandled promise rejections
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      logger.error('Unhandled promise rejection', event.reason);

      if (isDatabaseError(event.reason)) {
        event.preventDefault();
        handleDatabaseError(event.reason);
      }
    });

    // Handle global errors
    window.addEventListener('error', (event) => {
      logger.error('Global error', event.error);

      if (isDatabaseError(event.error)) {
        event.preventDefault();
        handleDatabaseError(event.error);
      }
    });
  }
}
